import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import {
  FaTimes,
  FaUserPlus,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaMapMarkedAlt,
  FaGlobe,
  FaExclamationTriangle,
  FaCheckSquare,
  FaSquare,
} from 'react-icons/fa';

export default function AddUserModal({ isOpen, onClose, onUserAdded }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.usertype === 0;

  const [hierarchy, setHierarchy] = useState({
    zones: [],
    ranges: [],
    districts: [],
    stations: [],
  });

  const [websitesList, setWebsitesList] = useState([]);
  const [selectedWebsites, setSelectedWebsites] = useState({});

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    password: '',
    usertype: '',
    mobileNo: '',
    alterMobileNo: '',
    emailId: '',
    zone_code: '',
    range_code: '',
    district_code: '',
    station_code: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch locations and websites when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedWebsites({});
      return;
    }

    const fetchInitialData = async () => {
      try {
        const fetches = [api.get('/hierarchy/locations')];
        if (isAdmin) {
          fetches.push(api.get('/websites'));
        }

        const [hierRes, webRes] = await Promise.all(fetches);

        if (hierRes.data.success) {
          setHierarchy(hierRes.data.data);

          if (currentUser?.usertype === 5 && hierRes.data.data.districts.length > 0) {
            setFormData((prev) => ({
              ...prev,
              district_code: String(hierRes.data.data.districts[0].code),
              usertype: '1',
            }));
          } else if (currentUser?.usertype === 0) {
            setFormData((prev) => ({
              ...prev,
              usertype: '1',
            }));
          }
        }

        if (webRes && webRes.data?.success) {
          setWebsitesList(webRes.data.websites || []);
          // Admin user does not select default anything when creating user
          setSelectedWebsites({});
        }
      } catch (err) {
        console.error('Failed to load initial form data:', err);
      }
    };

    fetchInitialData();
  }, [isOpen, currentUser, isAdmin]);

  if (!isOpen || ![0, 5].includes(currentUser?.usertype)) return null;

  const getAllowedUserTypes = () => {
    if (!currentUser) return [];
    const t = currentUser.usertype;

    if (t === 5) {
      // District: Can only add 51 (LIU), 52 (DCRB), and 1 (Station)
      return [
        { value: 1, label: 'Police Station (Type 1)' },
        { value: 51, label: 'LIU - Local Intelligence Unit (Type 51)' },
        { value: 52, label: 'DCRB - District Crime Records Bureau (Type 52)' },
      ];
    }

    if (t === 0) {
      return [
        { value: 0, label: 'Super Admin (Type 0)' },
        { value: 7, label: 'Zone - ADG/CP (Type 7)' },
        { value: 6, label: 'Range - DIG/IG/Addl CP (Type 6)' },
        { value: 5, label: 'District - SP/SSP/DCP (Type 5)' },
        { value: 51, label: 'LIU - District (Type 51)' },
        { value: 52, label: 'DCRB - District (Type 52)' },
        { value: 1, label: 'Police Station (Type 1)' },
      ];
    }

    return [];
  };

  // Filter ranges by selected zone code
  const filteredRanges = hierarchy.ranges.filter((r) => {
    if (!formData.zone_code) return false;
    return String(r.z_code) === String(formData.zone_code);
  });

  // Filter districts by selected range code
  const filteredDistricts = hierarchy.districts.filter((d) => {
    if (!formData.range_code) return false;
    return String(d.r_code) === String(formData.range_code);
  });

  // Filter stations by selected district code
  const filteredStations = hierarchy.stations.filter((s) => {
    if (!formData.district_code) return false;
    return String(s.district_code) === String(formData.district_code);
  });

  const selectedType = Number(formData.usertype);

  const toggleWebsite = (websiteId) => {
    setSelectedWebsites((prev) => ({
      ...prev,
      [websiteId]: !prev[websiteId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username.trim() || !formData.name.trim() || !formData.password.trim()) {
      setError('Username, Name, and Password are required.');
      return;
    }

    // Strong password validation
    const pwd = formData.password;
    const strongPwd = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>_+\-=]).{8,}$/;
    if (!strongPwd.test(pwd)) {
      setError(
        'Password must be at least 8 characters and include: one uppercase letter (A-Z), one lowercase letter (a-z), one number (0-9), and one special character (!@#$%...)'
      );
      return;
    }

    if (pwd !== confirmPassword) {
      setError('Password and Confirm Password must match.');
      return;
    }

    if (currentUser?.usertype === 0) {
      if (selectedType === 7 && !formData.zone_code) {
        setError('Please select a Zone.');
        return;
      }
      if (selectedType === 6 && !formData.range_code) {
        setError('Please select a Range.');
        return;
      }
      if ([5, 51, 52].includes(selectedType) && !formData.district_code) {
        setError('Please select a District.');
        return;
      }
      if (selectedType === 1 && !formData.station_code) {
        setError('Please select a Police Station.');
        return;
      }
    } else if (currentUser?.usertype === 5) {
      if (selectedType === 1 && !formData.station_code) {
        setError('Please select a Police Station.');
        return;
      }
    }

    setSaving(true);
    try {
      const allowedWebsiteIds = isAdmin
        ? Object.keys(selectedWebsites)
            .filter((id) => selectedWebsites[id])
            .map(Number)
        : undefined;

      const payload = {
        username: formData.username.trim(),
        name: formData.name.trim(),
        password: formData.password,
        usertype: selectedType,
        mobileNo: formData.mobileNo.trim() || null,
        alterMobileNo: formData.alterMobileNo.trim() || null,
        emailId: formData.emailId.trim() || null,
        zone_code: formData.zone_code ? Number(formData.zone_code) : undefined,
        range_code: formData.range_code ? Number(formData.range_code) : undefined,
        district_code: formData.district_code ? Number(formData.district_code) : undefined,
        station_code: formData.station_code ? Number(formData.station_code) : undefined,
        ...(isAdmin ? { websites: allowedWebsiteIds } : {}),
      };

      const res = await api.post('/users', payload);
      if (res.data.success) {
        setSelectedWebsites({});
        onUserAdded();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '640px' }}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(99, 102, 241, 0.2)',
                color: 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaUserPlus />
            </div>
            <div>
              <h3 style={{ fontSize: '1.2rem' }}>Add New User Account</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Hierarchy Assignment & Website Access Permissions
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.2rem',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ minWidth: '540px' }}>
            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  color: '#991b1b',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            )}

            {/* Role Selection */}
            <div className="form-group">
              <label className="form-label">
                <FaBuilding />
                <span>User Type / Role *</span>
              </label>
              <select
                className="form-control"
                value={formData.usertype}
                onChange={(e) => setFormData({ ...formData, usertype: e.target.value })}
                required
              >
                <option value="">Select User Type</option>
                {getAllowedUserTypes().map((ut) => (
                  <option key={ut.value} value={ut.value}>
                    {ut.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Multiple Websites Access Selector - Admin Only */}
            {isAdmin && (
              <div
                style={{
                  padding: '14px',
                  background: '#f8fafc',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '18px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: 'var(--primary)',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FaGlobe />
                  <span>Assign Website Access (Multiple Websites Allowed)</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {websitesList.map((site) => {
                    const isChecked = Boolean(selectedWebsites[site.id]);
                    return (
                      <div
                        key={site.id}
                        onClick={() => toggleWebsite(site.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 12px',
                          borderRadius: 'var(--radius-sm)',
                          background: isChecked ? '#eff6ff' : '#ffffff',
                          border: `1px solid ${isChecked ? '#93c5fd' : '#e2e8f0'}`,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <span style={{ color: isChecked ? 'var(--primary)' : 'var(--text-dim)', fontSize: '1.1rem' }}>
                          {isChecked ? <FaCheckSquare /> : <FaSquare />}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-main)' }}>
                            {site.website_name}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>
                            Code: {site.website_code}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cascading Jurisdiction Selectors for Admin */}
            {currentUser?.usertype === 0 && selectedType !== 0 && (
              <div
                style={{
                  padding: '14px',
                  background: '#f8fafc',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '18px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: 'var(--primary)',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FaMapMarkedAlt />
                  <span>Assign Jurisdiction Hierarchy (user_area_access)</span>
                </div>

                {/* Zone — always visible */}
                <div className="form-group">
                  <label className="form-label">Zone {selectedType === 7 ? '*' : ''}</label>
                  <select
                    className="form-control"
                    value={formData.zone_code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        zone_code: e.target.value,
                        range_code: '',
                        district_code: '',
                        station_code: '',
                      })
                    }
                    required={selectedType === 7}
                  >
                    <option value="">Select Zone</option>
                    {hierarchy.zones.map((z) => (
                      <option key={z.id} value={z.code}>
                        {z.name} {z.hindi_name ? `(${z.hindi_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Range — only show after Zone is selected, and only for types that need it */}
                {selectedType !== 7 && formData.zone_code && (
                  <div className="form-group">
                    <label className="form-label">Range {selectedType === 6 ? '*' : ''}</label>
                    <select
                      className="form-control"
                      value={formData.range_code}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          range_code: e.target.value,
                          district_code: '',
                          station_code: '',
                        })
                      }
                      required={selectedType === 6}
                    >
                      <option value="">Select Range</option>
                      {filteredRanges.map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* District — only show after Range is selected, and only for types that need it */}
                {[5, 51, 52, 1].includes(selectedType) && formData.range_code && (
                  <div className="form-group">
                    <label className="form-label">District *</label>
                    <select
                      className="form-control"
                      value={formData.district_code}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          district_code: e.target.value,
                          station_code: '',
                        })
                      }
                      required
                    >
                      <option value="">Select District</option>
                      {filteredDistricts.map((d) => (
                        <option key={d.code} value={d.code}>
                          {d.name} {d.hindi_name ? `(${d.hindi_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Station — only show after District is selected */}
                {selectedType === 1 && formData.district_code && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Police Station *</label>
                    <select
                      className="form-control"
                      value={formData.station_code}
                      onChange={(e) => setFormData({ ...formData, station_code: e.target.value })}
                      required
                    >
                      <option value="">Select Police Station</option>
                      {filteredStations.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name} {s.hindi_name ? `(${s.hindi_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* For District User (Type 5) */}
            {currentUser?.usertype === 5 && (
              <div
                style={{
                  padding: '14px',
                  background: '#f8fafc',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '18px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  District Scope:{' '}
                  <strong style={{ color: 'var(--text-main)' }}>
                    {currentUser.jurisdiction?.location_name || 'Your District'}
                  </strong>
                </div>

                {selectedType === 1 && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Select Police Station in your District *</label>
                    <select
                      className="form-control"
                      value={formData.station_code}
                      onChange={(e) => setFormData({ ...formData, station_code: e.target.value })}
                      required
                    >
                      <option value="">Choose Police Station</option>
                      {hierarchy.stations.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name} {s.hindi_name ? `(${s.hindi_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Input Details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label">Username * (10 Digits)</label>
                <input
                  type="text"
                  className="form-control"
                  maxLength={10}
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="945440XXXX"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Name / Office Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. SHO Sadar Agra"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaLock />
                <span>Account Password *</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  style={{ paddingRight: '44px' }}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 chars: A-Z, a-z, 0-9, !@#$..."
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px',
                  }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                Must have 8+ characters, 1 uppercase, 1 lowercase, 1 number &amp; 1 special character.
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaLock />
                <span>Confirm Account Password *</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  style={{ paddingRight: '44px' }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Enter the same password again"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((visible) => !visible)}
                  aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px',
                  }}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {confirmPassword && formData.password !== confirmPassword && (
                <span style={{ fontSize: '0.72rem', color: '#b91c1c', marginTop: '4px' }}>
                  Passwords do not match.
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label">
                  <FaPhone />
                  <span>Primary Mobile</span>
                </label>
                <input
                  type="text"
                  maxLength={10}
                  className="form-control"
                  value={formData.mobileNo}
                  onChange={(e) => setFormData({ ...formData, mobileNo: e.target.value })}
                  placeholder="10-digit mobile"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaEnvelope />
                  <span>Email ID</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.emailId}
                  onChange={(e) => setFormData({ ...formData, emailId: e.target.value })}
                  placeholder="user@domain.gov.in"
                />
              </div>
            </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Registering...' : 'Register User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
